/**
 * Site Chart Registry
 * 
 * This registry manages chart configurations for technology-specific site charts.
 * Currently supports LTE with a placeholder for NR (5G) charts.
 * 
 * Technology-based chart grouping enables:
 * - Dynamic chart rendering based on device type (nodeType)
 * - Easy addition of new technologies (NR, 5G-Advanced, 6G, etc.)
 * - Centralized chart configuration
 * - Elimination of hardcoded chart JSX
 * 
 * @module Performance/Site
 */

import { ChartGroupConfig } from '../chartGroupConfigs';

// Import LTE chart components from technology-specific folder
import {
  ErabEstablishmentEcharts,
  VolteEstablishmentEcharts,
  RrcConnectionEstablishmentEcharts,
  ErabDropRateEcharts,
  VoLteDropRateEcharts,
  HandinOverS1Echarts,
  HandoutOverS1Echarts,
  IntraFreqX1HoEcharts,
  InterFreqX1HoEcharts,
  DlGtpDataVolumeEcharts,
  UlGtpDataVolumeEcharts,
  MaximumEndcEcharts,
  SgNbAdditionEcharts
} from '../../LTE';

// Import NR chart wrapper components (thin wrappers that use SiteCharts template with NR configs)
import { 
  SgNbAdditionNrEcharts, 
  MaximumEndcNrEcharts, 
  EndcUeSessionDropNrEcharts,
  DlGtpDataVolumeNrEcharts,
  UlGtpDataVolumeNrEcharts,
  IntraCuHandoverNrEcharts
} from '../../NR';

// Import SA (NR Standalone) chart wrapper components
import {
  RrcEstablishmentSaEcharts,
  SignalingConnectionSaEcharts,
  DataAccessibilityPer5QiSaEcharts,
  DataRetainabilityPer5QiSaEcharts,
  UeCallDropRateSaEcharts,
  InterCuHandoutSaEcharts,
  InterCuHandinSaEcharts,
  InterCuIntraFreqHandoutSaEcharts,
  InterCuInterFreqHandoutSaEcharts,
  GtpDataVolumeSaEcharts,
} from '../../SA';

/**
 * LTE Chart Groups Configuration
 * 
 * Organizes 13 LTE charts into 5 logical groups by KPI category:
 * - Accessibility (3 charts): Connection establishment metrics
 * - Retainability (2 charts): Connection drop rate metrics
 * - Handover (4 charts): Handover success rate metrics
 * - Data Volume (2 charts): Data transfer volume metrics
 * - ENDC (2 charts): EN-DC (LTE-NR dual connectivity) metrics
 */
export const LTE_CHART_GROUPS: ChartGroupConfig[] = [
  {
    groupTitle: "Accessibility",
    charts: [
      {
        component: ErabEstablishmentEcharts,
        fullscreenKey: 'erabE',
        // gridSize is optional - defaults to getChartGridSize() calculation
      },
      {
        component: VolteEstablishmentEcharts,
        fullscreenKey: 'volteE',
      },
      {
        component: RrcConnectionEstablishmentEcharts,
        fullscreenKey: 'rrcE',
      },
    ],
  },
  {
    groupTitle: "Retainability",
    charts: [
      {
        component: ErabDropRateEcharts,
        fullscreenKey: 'erabDropRate',
      },
      {
        component: VoLteDropRateEcharts,
        fullscreenKey: 'volteDropRate',
      },
    ],
  },
  {
    groupTitle: "Handover",
    charts: [
      {
        component: HandinOverS1Echarts,
        fullscreenKey: 'handinOverS1',
      },
      {
        component: HandoutOverS1Echarts,
        fullscreenKey: 'handoutOverS1',
      },
      {
        component: IntraFreqX1HoEcharts,
        fullscreenKey: 'intraFreqX1Ho',
      },
      {
        component: InterFreqX1HoEcharts,
        fullscreenKey: 'interFreqX1Ho',
      },
    ],
  },
  {
    groupTitle: "Data Volume",
    charts: [
      {
        component: DlGtpDataVolumeEcharts,
        fullscreenKey: 'dlGtpDataVolume',
      },
      {
        component: UlGtpDataVolumeEcharts,
        fullscreenKey: 'ulGtpDataVolume',
      },
    ],
  },
  {
    groupTitle: "ENDC",
    charts: [
      {
        component: MaximumEndcEcharts,
        fullscreenKey: 'maximumEndc',
      },
      {
        component: SgNbAdditionEcharts,
        fullscreenKey: 'sgNbAddition',
      },
    ],
  },
];

/**
 * NR Chart Groups Configuration (Placeholder)
 * 
 * Currently empty - will be populated when NR charts are implemented.
 * See DeviceDetail/NR/README.md for implementation guide.
 * 
 * Example structure when NR is implemented:
 * ```typescript
 * export const NR_CHART_GROUPS: ChartGroupConfig[] = [
 *   {
 *     groupTitle: "NR Accessibility",
 *     charts: [
 *       {
 *         component: NrAccessibilityEcharts,
 *         fullscreenKey: 'nrAccessibility',
 *       },
 *       // ... more NR accessibility charts
 *     ],
 *   },
 *   // ... more NR chart groups
 * ];
 * ```
 */
export const NR_CHART_GROUPS: ChartGroupConfig[] = [
  {
    groupTitle: "Accessibility",
    groupGridSize: { xs: 12, md: 6 },  // Half width on desktop, full width on mobile
    charts: [
      {
        component: SgNbAdditionNrEcharts,
        fullscreenKey: 'sgNbAdditionNr',
      }
    ]
  },
  {
    groupTitle: "Capacity",
    groupGridSize: { xs: 12, md: 6 },  // Half width on desktop, full width on mobile
    charts: [
      {
        component: MaximumEndcNrEcharts,
        fullscreenKey: 'maximumEndcNr',
      }
    ]
  },
  {
    groupTitle: "Retainability",
    charts: [
      {
        component: EndcUeSessionDropNrEcharts,
        fullscreenKey: 'endcUeSessionDropNr',
      }
    ]
  },
  {
    groupTitle: "Data Volume",
    charts: [
      {
        component: DlGtpDataVolumeNrEcharts,
        fullscreenKey: 'dlGtpDataVolumeNr',
      },
      {
        component: UlGtpDataVolumeNrEcharts,
        fullscreenKey: 'ulGtpDataVolumeNr',
      }
    ]
  },
  {
    groupTitle: "Handover",
    charts: [
      {
        component: IntraCuHandoverNrEcharts,
        fullscreenKey: 'intraCuHandoverNr',
      }
    ]
  }
];

/**
 * Technology Type
 * Defines supported radio access technologies
 */
export type TechnologyType = 'LTE' | 'NR';

/**
 * Site Chart Registry
 * Maps technology type to its corresponding chart groups
 */
export const SITE_CHART_REGISTRY: Record<TechnologyType, ChartGroupConfig[]> = {
  'LTE': LTE_CHART_GROUPS,
  'NR': NR_CHART_GROUPS,
};

/**
 * Get Technology from Node Type
 * 
 * Maps device node type to radio access technology.
 * 
 * @param nodeType - Device node type (SN, BBU, RCP, CU, etc.)
 * @returns Technology type ('LTE' or 'NR')
 * 
 * @example
 * getTechnologyFromNodeType('SN')   // Returns 'LTE'
 * getTechnologyFromNodeType('BBU')  // Returns 'LTE'
 * getTechnologyFromNodeType('RCP')  // Returns 'NR'
 * getTechnologyFromNodeType('CU')   // Returns 'NR'
 */
export function getTechnologyFromNodeType(nodeType: string): TechnologyType {
  const normalizedType = nodeType?.toUpperCase() || '';
  
  // NR device types
  if (normalizedType === 'RCP' || normalizedType === 'CU') {
    return 'NR';
  }
  
  // LTE device types (SN, BBU) - also default
  return 'LTE';
}

/**
 * Get Site Chart Groups
 * 
 * Returns the appropriate chart groups based on device type.
 * Only returns charts that match the device's technology (LTE or NR).
 * 
 * @param nodeType - Device node type
 * @returns Array of chart group configurations
 * 
 * @example
 * const charts = getSiteChartGroups('SN');  // Returns LTE_CHART_GROUPS
 * const charts = getSiteChartGroups('RCP'); // Returns NR_CHART_GROUPS (empty if not implemented)
 */
export function getSiteChartGroups(nodeType: string): ChartGroupConfig[] {
  const technology = getTechnologyFromNodeType(nodeType);
  return SITE_CHART_REGISTRY[technology];
}

/**
 * Get Site Fullscreen Keys
 * 
 * Returns all fullscreen keys for the specified device type.
 * Useful for initializing fullscreen state or validation.
 * 
 * @param nodeType - Device node type
 * @returns Array of fullscreen key strings
 * 
 * @example
 * const keys = getSiteFullscreenKeys('SN');
 * // Returns: ['erabE', 'volteE', 'rrcE', 'erabDropRate', ...]
 */
export function getSiteFullscreenKeys(nodeType: string): string[] {
  const chartGroups = getSiteChartGroups(nodeType);
  const keys: string[] = [];
  
  chartGroups.forEach(group => {
    group.charts.forEach(chart => {
      keys.push(chart.fullscreenKey);
    });
  });
  
  return keys;
}

/**
 * Get Site Chart Count
 * 
 * Returns the total number of charts for the specified device type.
 * 
 * @param nodeType - Device node type
 * @returns Total number of charts
 * 
 * @example
 * const count = getSiteChartCount('SN');  // Returns 13 (for LTE)
 */
export function getSiteChartCount(nodeType: string): number {
  const chartGroups = getSiteChartGroups(nodeType);
  let count = 0;
  
  chartGroups.forEach(group => {
    count += group.charts.length;
  });
  
  return count;
}

// ============================================================
// SA (NR Standalone) Site Chart Groups — additive, separate from SITE_CHART_REGISTRY
// ============================================================

export const SA_CHART_GROUPS: ChartGroupConfig[] = [
  {
    groupTitle: "Accessibility",
    charts: [
      { component: RrcEstablishmentSaEcharts,        fullscreenKey: 'saRrcEstablishment' },
      { component: SignalingConnectionSaEcharts,     fullscreenKey: 'saSignalingConnection' },
      { component: DataAccessibilityPer5QiSaEcharts, fullscreenKey: 'saDataAccessibilityPer5Qi' },
    ],
  },
  {
    groupTitle: "Retainability",
    charts: [
      { component: DataRetainabilityPer5QiSaEcharts, fullscreenKey: 'saDataRetainabilityPer5Qi' },
      { component: UeCallDropRateSaEcharts,          fullscreenKey: 'saUeCallDropRate' },
    ],
  },
  {
    groupTitle: "Handover",
    charts: [
      { component: InterCuHandoutSaEcharts,          fullscreenKey: 'saInterCuHandout' },
      { component: InterCuHandinSaEcharts,           fullscreenKey: 'saInterCuHandin' },
      { component: InterCuIntraFreqHandoutSaEcharts, fullscreenKey: 'saInterCuIntraFreqHandout' },
      { component: InterCuInterFreqHandoutSaEcharts, fullscreenKey: 'saInterCuInterFreqHandout' },
    ],
  },
  {
    groupTitle: "Data Volume",
    charts: [
      { component: GtpDataVolumeSaEcharts,           fullscreenKey: 'saGtpDataVolume' },
    ],
  },
];

export function getSaSiteChartGroups(): ChartGroupConfig[] {
  return SA_CHART_GROUPS;
}

export function getSaSiteFullscreenKeys(): string[] {
  return SA_CHART_GROUPS.flatMap(group => group.charts.map(chart => chart.fullscreenKey));
}
